import React, { useState, useEffect } from 'react';
import Header from '@/components/Header';
import ManufacturerLogos from '@/components/ManufacturerLogos';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MapPin, ArrowRight, Filter } from 'lucide-react';
import { realDroneInventory, categories, brands, priceRanges } from '@/data/realDroneInventory';
import { formatPrice } from '@/utils/priceFormatter';
import { useNavigate } from 'react-router-dom';
import Footer from '@/components/sections/Footer';

export default function MarketplacePage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [selectedBrand, setSelectedBrand] = useState('All Brands');
  const [selectedPriceRange, setSelectedPriceRange] = useState(priceRanges[0]);
  const [filteredDrones, setFilteredDrones] = useState(realDroneInventory);
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('featured');

  // Update header search when component search changes
  const handleSearchUpdate = (query) => {
    setSearchQuery(query);
  };

  useEffect(() => {
    let results = [...realDroneInventory];

    // Filter by search query
    if (searchQuery) {
      results = results.filter((drone) =>
        drone.model.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Filter by category
    if (selectedCategory !== 'All Categories') {
      results = results.filter((drone) => drone.category === selectedCategory);
    }

    // Filter by brand
    if (selectedBrand !== 'All Brands') {
      results = results.filter((drone) => drone.brand === selectedBrand);
    }

    // Filter by price range
    results = results.filter(
      (drone) => drone.price >= selectedPriceRange.min && drone.price <= selectedPriceRange.max
    );

    // Sort results
    if (sortBy === 'price-low') {
      results.sort((a, b) => a.price - b.price);
    } else if (sortBy === 'price-high') {
      results.sort((a, b) => b.price - a.price);
    } else if (sortBy === 'featured') {
      results.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
    }

    setFilteredDrones(results);
  }, [searchQuery, selectedCategory, selectedBrand, selectedPriceRange, sortBy]);

  const resetFilters = () => {
    setSearchQuery('');
    setSelectedCategory('All Categories');
    setSelectedBrand('All Brands');
    setSelectedPriceRange(priceRanges[0]);
  };

  return (
    <div className="min-h-screen">
      <Header onSearch={handleSearchUpdate} searchValue={searchQuery} />
      <ManufacturerLogos />

      {/* Hero section */}
      <section className="bg-gradient-to-b from-muted/50 to-background py-12 sm:py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">
              Browse Industrial Drones
            </h1>
            <p className="text-lg text-muted-foreground">
              Discover {realDroneInventory.length} professional UAVs from trusted vendors
            </p>
          </div>
        </div>
      </section>

      {/* Marketplace Grid */}
      <section className="py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Filters Sidebar */}
            <aside className="lg:w-64 flex-shrink-0">
              {/* Mobile filter toggle */}
              <Button
                variant="outline"
                className="lg:hidden w-full mb-4"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="w-4 h-4 mr-2" />
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </Button>

              <div className={`space-y-6 ${showFilters ? 'block' : 'hidden lg:block'}`}>
                {/* Category Filter */}
                <div>
                  <h3 className="font-semibold mb-3">Category</h3>
                  <RadioGroup value={selectedCategory} onValueChange={setSelectedCategory}>
                    {categories.map((category) => (
                      <div key={category} className="flex items-center space-x-2">
                        <RadioGroupItem value={category} id={`cat-${category}`} />
                        <Label htmlFor={`cat-${category}`} className="cursor-pointer">
                          {category}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                {/* Brand Filter */}
                <div>
                  <h3 className="font-semibold mb-3">Brand</h3>
                  <RadioGroup value={selectedBrand} onValueChange={setSelectedBrand}>
                    {brands.map((brand) => (
                      <div key={brand} className="flex items-center space-x-2">
                        <RadioGroupItem value={brand} id={`brand-${brand}`} />
                        <Label htmlFor={`brand-${brand}`} className="cursor-pointer">
                          {brand}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                {/* Price Range Filter */}
                <div>
                  <h3 className="font-semibold mb-3">Price Range</h3>
                  <RadioGroup
                    value={selectedPriceRange.label}
                    onValueChange={(label) => {
                      const range = priceRanges.find((r) => r.label === label);
                      setSelectedPriceRange(range);
                    }}
                  >
                    {priceRanges.map((range) => (
                      <div key={range.label} className="flex items-center space-x-2">
                        <RadioGroupItem value={range.label} id={`price-${range.label}`} />
                        <Label htmlFor={`price-${range.label}`} className="cursor-pointer">
                          {range.label}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                {/* Reset Filters */}
                <Button variant="outline" className="w-full" onClick={resetFilters}>
                  Reset Filters
                </Button>
              </div>
            </aside>

            {/* Results Grid */}
            <div className="flex-1">
              {/* Results count and sort */}
              <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
                <p className="text-muted-foreground">
                  Showing {filteredDrones.length} of {realDroneInventory.length} drones
                </p>
                <div className="flex items-center gap-2">
                  <Label htmlFor="sort" className="text-sm">Sort by:</Label>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger id="sort" className="w-[180px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="featured">Featured</SelectItem>
                      <SelectItem value="price-low">Price: Low to High</SelectItem>
                      <SelectItem value="price-high">Price: High to Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Grid */}
              {filteredDrones.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredDrones.map((drone) => (
                    <Card
                      key={drone.id}
                      className="group overflow-hidden hover:shadow-xl transition-all duration-300 border-border hover:border-primary/50 flex flex-col cursor-pointer"
                      onClick={() => navigate(`/drone/${drone.id}`)}
                    >
                      {/* Image */}
                      <div className="relative h-56 overflow-hidden bg-muted">
                        <img
                          src={drone.image}
                          alt={drone.model}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          loading="lazy"
                        />
                        <div className="absolute top-3 left-3 flex gap-2">
                          {drone.featured && (
                            <Badge className="bg-accent text-accent-foreground">Featured</Badge>
                          )}
                          <Badge variant="secondary">{drone.condition}</Badge>
                        </div>
                      </div>

                      <CardHeader className="flex-grow">
                        <CardTitle className="text-xl line-clamp-1 group-hover:text-primary transition-colors">
                          {drone.model}
                        </CardTitle>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <MapPin className="w-4 h-4" />
                          {drone.location}
                        </div>
                        <CardDescription className="line-clamp-2 mt-2">
                          {drone.description}
                        </CardDescription>
                      </CardHeader>

                      <CardContent>
                        {/* Specs */}
                        <div className="flex flex-wrap gap-2">
                          <Badge variant="outline" className="text-xs">
                            {drone.specs.flightTime}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {drone.specs.payload}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {drone.category}
                          </Badge>
                        </div>
                      </CardContent>

                      <CardFooter className="flex items-center justify-between pt-4 border-t mt-auto">
                        <div>
                          <div className="text-2xl font-bold text-primary">
                            {formatPrice(drone.price, drone.currency)}
                          </div>
                          <div className="text-xs text-muted-foreground">{drone.vendor}</div>
                        </div>
                        <Button size="sm" className="group/btn">
                          View Details
                          <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                        </Button>
                      </CardFooter>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card className="p-12 text-center">
                  <h3 className="text-2xl font-bold mb-4">No Drones Found</h3>
                  <p className="text-muted-foreground mb-6">
                    We couldn't find any drones matching your criteria. Try adjusting your filters.
                  </p>
                  <div className="flex gap-4 justify-center">
                    <Button onClick={resetFilters}>Reset Filters</Button>
                    <Button
                      variant="outline"
                      onClick={() => navigate('/')}
                    >
                      Back to Home
                    </Button>
                  </div>
                </Card>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}